import type { ReactElement, ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { BasicModal, Button } from "@similarweb/ui-components";

import {
    FILTER_CONDITION_DIALOG_STYLES,
    StyledModalButtons,
    StyledModalContent,
    StyledModalHeader,
    StyledModalSubtitle,
    StyledModalTitle,
} from "./styles";

const HIDDEN_CLOSE_ICON = { show: false, color: colorsPalettes.carbon[0], onClick: () => {} } as const;

export type FilterConditionDialogProps = {
    readonly isOpen: boolean;
    readonly onRequestClose: () => void;
    /** Automation prefix — the modal itself gets this id; Cancel/Apply derive `${dataAutomation}-cancel` / `-apply`. */
    readonly dataAutomation: string;
    readonly title?: ReactNode;
    readonly description?: ReactNode;
    readonly onCancel: () => void;
    readonly onApply: () => void;
    readonly isApplyDisabled?: boolean;
    readonly children: ReactNode;
};

/**
 * Shared chrome for a filter's add/edit dialog: the `BasicModal` wrapper, header title/subtitle, and Cancel/Apply
 * footer. Each filter supplies its own widget markup as children, so the dialogs stay visually identical by
 * construction instead of by manually mirroring styles. Shared by the Technologies filter and the compound
 * (per-condition) CRM group filter.
 */
export const FilterConditionDialog = ({
    isOpen,
    onRequestClose,
    dataAutomation,
    title,
    description,
    onCancel,
    onApply,
    isApplyDisabled = false,
    children,
}: FilterConditionDialogProps): ReactElement => {
    const { translate } = useTranslate();

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            customStyles={FILTER_CONDITION_DIALOG_STYLES}
            closeIconConfig={HIDDEN_CLOSE_ICON}
            dataAutomation={dataAutomation}
        >
            {title || description ? (
                <StyledModalHeader>
                    {title ? <StyledModalTitle>{title}</StyledModalTitle> : null}
                    {description ? <StyledModalSubtitle>{description}</StyledModalSubtitle> : null}
                </StyledModalHeader>
            ) : null}

            <StyledModalContent>{children}</StyledModalContent>

            <StyledModalButtons>
                <Button
                    type="flat"
                    onClick={onCancel}
                    dataAutomation={`${dataAutomation}-cancel`}
                >
                    {translate("si.common.button.cancel")}
                </Button>
                <Button
                    onClick={onApply}
                    isDisabled={isApplyDisabled}
                    dataAutomation={`${dataAutomation}-apply`}
                >
                    {translate("common.apply")}
                </Button>
            </StyledModalButtons>
        </BasicModal>
    );
};
